// React and Related imports
import React, { useEffect, useState } from 'react';

// i18n
import { t } from 'i18next';

// React Router
import { Outlet, useLocation } from 'react-router-dom';

// Auth Context
import { useAuth } from './AuthProvider';

// Custom Components
import { Layout } from '../components/Layout/Layout';
import { Loading } from '../components/Shared/Loading/Loading';

// Troubleshooting Svg
import Troubleshooting from '../assets/images/computer-troubleshooting-animate.svg';

const MAX_ATTEMPTS = 4;

export const AuthorizedRoute: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [countdown, setCountdown] = useState(3);

  const [attempts] = useState(() =>
    parseInt(sessionStorage.getItem('loginAttempts') || '0', 10)
  );

  useEffect(() => {
    // Clean up any stale data from previous sessions
    sessionStorage.removeItem('initialPath');
    sessionStorage.setItem('loginAttempts', '0');
  }, []);

  useEffect(() => {
    if (!isAuthenticated && !loading) {
      if (attempts >= MAX_ATTEMPTS) return;

      let countdownTimer: ReturnType<typeof setInterval>;
      let redirectTimer: ReturnType<typeof setTimeout>;

      if (countdown > 0) {
        countdownTimer = setInterval(() => {
          setCountdown((prev) => prev - 1);
        }, 1000);
      }

      redirectTimer = setTimeout(() => {
        sessionStorage.setItem('loginAttempts', (attempts + 1).toString());
        // Store the current path to redirect back after authentication
        const pathToStore = location.pathname + location.search;
        sessionStorage.setItem('redirectAfterAuth', pathToStore);

        window.location.replace(
          `${process.env.REACT_APP_BACKEND_URL}/dashboard-auth/google`
        );
      }, 3000);

      return () => {
        clearInterval(countdownTimer);
        clearTimeout(redirectTimer);
      };
    }
  }, [isAuthenticated, loading]);

  if (loading)
    return (
      <div className="flex h-screen flex-col items-center justify-center text-center">
        <h1 className="mb-4 text-4xl font-extrabold leading-none tracking-tight text-white md:text-4xl lg:text-5xl">
          {t('auth.checking')}
        </h1>
        <Loading />
      </div>
    );

  if (!isAuthenticated) {
    if (attempts >= MAX_ATTEMPTS) {
      return (
        <div className="flex h-screen flex-col items-center justify-center pt-20 text-center">
          <h1 className="mb-4 text-4xl font-extrabold leading-none tracking-tight text-white md:text-4xl lg:text-5xl">
            {t('auth.failedTitle')}
            <span className="text-red-500">{t('auth.failedEmphasis')}</span>
          </h1>
          <p className="mb-6 text-lg font-normal text-gray-200 sm:px-16 lg:text-xl xl:px-48">
            {t('auth.failedHint')}
          </p>
          <img
            src={Troubleshooting}
            alt={t('auth.troubleshootingAlt')}
            className="h-auto w-full max-w-lg"
          />
        </div>
      );
    }

    return (
      <div className="flex h-screen items-center justify-center text-center">
        <h1 className="mb-4 text-4xl font-extrabold leading-none tracking-tight text-white md:text-4xl lg:text-5xl">
          {attempts > 0
            ? t('auth.retrying', { attempt: attempts })
            : t('auth.redirecting', { seconds: countdown })}
        </h1>
      </div>
    );
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};
